import { DataTable } from '@/components/ui/data-table'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { InvoiceMobileList } from '@/domains/invoice'
import { useServerTable } from '@/lib/use-server-table'

import {
  formatAmount,
  InvoiceBadge,
  invoiceColumns,
  InvoiceLink
} from './invoice-columns'
import { formatBillingDate } from './plan-price'
import { INVOICES_DEFAULT_PAGE_SIZE, useInvoices } from './use-invoices'

/* this table sorts on the client, so only its page goes in the url. */
const CLIENT_SORTED: readonly string[] = []

/* companyId puts it in super scope, where the copy is about a company rather
 * than the caller. */
export function InvoiceList({ companyId }: { companyId?: string } = {}) {
  const table = useServerTable({
    defaultPageSize: INVOICES_DEFAULT_PAGE_SIZE,
    url: { sortFields: CLIENT_SORTED }
  })
  const query = useInvoices(table.page, table.pageSize, companyId)

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0

  if (query.isLoading) return <Skeleton className='h-40 w-full' />
  if (query.isError) {
    return (
      <div className='rounded-md border border-destructive/40 bg-destructive/5 p-4 text-[13px] text-destructive'>
        {companyId
          ? 'Couldn’t load this company’s payments. Refresh and try again.'
          : 'Couldn’t load your payments. Refresh and try again.'}
      </div>
    )
  }

  const emptyMessage = companyId
    ? 'No payments yet. This company has never been billed.'
    : 'No payments yet. Invoices and receipts appear here once you subscribe.'

  return (
    <div className='flex flex-col gap-4'>
      <DataTable
        columns={invoiceColumns(companyId)}
        data={items}
        emptyMessage={emptyMessage}
      />
      <InvoiceMobileList
        items={items.map(invoice => ({
          id: invoice.id,
          title: invoice.number ?? '—',
          subtitle: `Issued ${formatBillingDate(invoice.createdAt)}`,
          amount: formatAmount(invoice.amountDueCents, invoice.currency),
          meta: [
            invoice.paidAt ? `Paid ${formatBillingDate(invoice.paidAt)}` : null,
            invoice.amountCreditedCents > 0
              ? `Refunded −${formatAmount(invoice.amountCreditedCents, invoice.currency)}`
              : null
          ]
            .filter(Boolean)
            .join(' · '),
          badge: <InvoiceBadge invoice={invoice} />,
          action: <InvoiceLink invoice={invoice} companyId={companyId} />
        }))}
        emptyMessage={emptyMessage}
      />
      <DataTablePagination
        page={table.page}
        pageSize={table.pageSize}
        total={total}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  )
}
